import { TableCheckbox } from "../../Checkbox/Checkbox";
import { useCallback, useMemo, useState } from "react";
import { AllSelectedCheckStates, CellCustomDataType, ColumnType, UseSelectOutputType, UseSelectPropType } from "../types";

const useTableSelect = (props: UseSelectPropType): UseSelectOutputType => {

    const { columns,
        data,
        uniqueIdAccessor,
        selectAccessor = 'isSelect',
        onSelectRow,
        onSelectAll,
        getLabeledById,
        labeledByAccessor,
        selectAllState
    } = props;

    const [selectedRows, setSelectedRows] = useState<any[]>([]);

    /** Validating all required accessors */
    if (!uniqueIdAccessor || !selectAccessor) {
        throw new Error("Selectable table should have uniqueIdKey(which will define an unique value in table data) and selectableKey(accessor name of the column)")
    }

    if (onSelectRow === undefined || onSelectAll === undefined) {
        throw new Error("Please define on onSelectRow and onSelectAll both functions are required");
    }

    if (!labeledByAccessor) {
        console.warn('labeledByAccessor is required to work properly in Screen reader.')
    }

    let mCols: ColumnType[] = [...columns];

    const getIDValue = useCallback((cell: CellCustomDataType) => {
        if (getLabeledById) {
            return getLabeledById(cell)
        }
        return `${labeledByAccessor}-${cell.origin[uniqueIdAccessor]}`;
    }, [labeledByAccessor, uniqueIdAccessor]);

    /** Internal onSelect function, which selects specific rows */
    const onSelectFunction = (columnData: CellCustomDataType, e: React.ChangeEvent<HTMLInputElement>) => {

        const { origin } = columnData;
        let anyChecked = false;
        let checkedCount = 0;

        const modifiedData = [...data].map((item) => {
            const checked = (item[uniqueIdAccessor] === origin[uniqueIdAccessor]) ? e.target.checked : item[selectAccessor];

            if (checked) {
                checkedCount++;
                anyChecked = true
            }

            return {
                ...item,
                [selectAccessor]: checked
            }
        });

        const everyChecked = checkedCount === modifiedData.length;

        /** This block will check if any item checked or all item checked, depending on that it will decide the internal selectAll state */

        let sas = selectAllState;

        if (everyChecked) {
            sas = 'checked';
        } else if (anyChecked) {
            sas = 'indeterminate';
        } else {
            sas = 'unchecked';
        }

        const updatedSelectedRows = updateSelectedState(origin);

        onSelectRow && onSelectRow({ ...columnData, updated: modifiedData, selectAllState: sas, selectedRows: updatedSelectedRows }, e);
    }

    /** Internal Select All function, which selecting all rows */
    const onSelectAllFunction = (columnData: ColumnType, e: React.ChangeEvent<HTMLInputElement>) => {
        const ss: AllSelectedCheckStates = e.target.checked ? 'checked' : 'unchecked';

        const updatedData = data.map(item => ({
            ...item,
            [selectAccessor]: (ss === 'checked')
        }));

        const updatedSelectedRows: any[] = ss === 'checked' ? [...updatedData] : [];

        setSelectedRows(updatedSelectedRows);

        onSelectAll && onSelectAll({ ...columnData, updated: updatedData, selectAllState: ss, selectedRows: updatedSelectedRows }, e);
    }

    // update internal selected rows
    const updateSelectedState = (item: any) => {

        const index = selectedRows.findIndex(_i => _i[uniqueIdAccessor] === item[uniqueIdAccessor])
        let updatedSelectedRows = [...selectedRows];

        if (index < 0 && item[selectAccessor] === true) {
            updatedSelectedRows = [...updatedSelectedRows, item];
            setSelectedRows(updatedSelectedRows);
        }
        else if (index >= 0 && item[selectAccessor] === false) {
            updatedSelectedRows = [...updatedSelectedRows, item].splice(index, 1);
            setSelectedRows(updatedSelectedRows)
        }

        return updatedSelectedRows;
    }

    /** Dynamically adding the header and cell checkboxes, using uniqueIdAccessor and selectAccessor */
    mCols = [{
        Header: (col: ColumnType) => {
            const labeledById = `${col.accessor}-select-all`;
            return <>
                <TableCheckbox
                    onChange={(e: any) => {
                        onSelectAllFunction(col, e);
                    }}
                    checked={selectAllState === 'checked'}
                    indeterminate={selectAllState === 'indeterminate'}
                    ariaLabeledBy={labeledById}
                    data-testid={`ted-ui-select-all`} />
                <span id={labeledById} className="sr-only">Select all rows</span>
            </>
        },
        Cell: (cell: CellCustomDataType) => {
            return <TableCheckbox ariaLabeledBy={getIDValue(cell)}
                onChange={(e: any) => {
                    onSelectFunction(cell, e);
                }}
                checked={cell.origin[selectAccessor]} data-testid={`select-row-${cell.origin[uniqueIdAccessor]}`} />
        },
        isSelectColumn: true,
        accessor: selectAccessor as string,
    }, ...mCols].map(item => ({
        ...item,
        ID: getLabeledById ? undefined : (labeledByAccessor === item.accessor) ? getIDValue : undefined
    }));


    return useMemo(() => ({
        columns: mCols,
        uniqueIdAccessor,
        selectAccessor,
        labeledByAccessor,
    }), [uniqueIdAccessor, selectAccessor, labeledByAccessor, selectAllState, data, columns]);
}

export default useTableSelect;